// What a running Assessment asks, read-only. Opened by Continue on the
// Assessment's card on the dashboard, it is the page the manager saw at the
// last step of Run Assessment: Elliott's Roleplay and Quiz Question for each
// capability, then the message to the employee. Nothing here can be changed:
// an Assessment is graded as it was run.

import React from 'react';
import { Box, Dialog, Fade, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { AdminAssessmentView } from '../../../../api/capability';
import type { AssessmentCardInfo } from '../Program/components/learning-lifecycle/adminAssessmentCards';
import { AssessmentDraftView, MessageToEmployee } from './create/AssessmentDraftView';
import { ActionButton, FlowStep, Note, Stepper } from './designSystem';

/** Run Assessment's steps: this is its last one, as it was left when the Assessment ran. */
const STEPS = ['Details', 'Capabilities', 'Summary', 'Review'];

const headline = (status: AssessmentCardInfo) => {
  if (status.status === 'Closed') return 'This Assessment is closed';
  if (status.status === 'Published') return 'Published, waiting for its people';
  return status.status === 'Running' ? `Running since ${status.startDate}` : `Starts on ${status.startDate}`;
};

const RESULTS = 'Results will appear on the Team Capability tab on the Dashboard.';

/** The note above what it asks: where the Assessment stands, and where its results are read. */
const standing = (status: AssessmentCardInfo) => {
  if (status.status === 'Closed')
    return 'This Assessment is closed. You can see its results on the Team Capability tab on the Dashboard.';
  if (status.status === 'Published') return status.stage;
  return status.status === 'Running'
    ? `This Assessment is active. ${RESULTS}`
    : `This Assessment starts on ${status.startDate}. ${RESULTS}`;
};

const AssessmentReviewDialog: React.FC<{
  /** The Assessment to show; null closes the dialog. */
  assessment: AdminAssessmentView | null;
  /** Its card's title. */
  title: string;
  /** Where its run stands, as its card has it. */
  status: AssessmentCardInfo | null;
  onClose: () => void;
}> = ({ assessment, title, status, onClose }) => {
  // Hold what was open through the fade-out, after the props have gone back to nothing.
  const [shown, setShown] = React.useState<{
    assessment: AdminAssessmentView;
    title: string;
    status: AssessmentCardInfo;
  } | null>(null);
  React.useEffect(() => {
    if (assessment && status) setShown({ assessment, title, status });
  }, [assessment, title, status]);

  return (
    <Dialog
      open={Boolean(assessment)}
      onClose={onClose}
      maxWidth={false}
      TransitionComponent={Fade}
      transitionDuration={{ enter: 260, exit: 180 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }
        }
      }}
      PaperProps={{
        sx: {
          width: '96vw',
          maxWidth: '96vw',
          height: '94vh',
          m: 2,
          bgcolor: '#0F0F0F',
          border: '1px solid #444444',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundImage: 'none'
        }
      }}
    >
      <Box
        sx={{
          px: '24px',
          py: '16px',
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0
        }}
      >
        <Typography sx={{ flex: 1, color: '#FDFDFD', fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 700 }}>
          {shown?.title}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ color: '#B6B6B6', '&:hover': { color: '#FDFDFD' } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {shown && (
          <FlowStep
            stepper={<Stepper steps={STEPS} current={STEPS.length - 1} />}
            bar={{
              done: true,
              title: headline(shown.status),
              body: `${shown.status.completion} It is shown as it was run and cannot be edited.`,
              actions: (
                <ActionButton primary onClick={onClose}>
                  Close
                </ActionButton>
              )
            }}
          >
            <Note>{standing(shown.status)}</Note>
            <AssessmentDraftView items={shown.assessment.items} readOnly />
            <MessageToEmployee value={shown.assessment.message} readOnly />
          </FlowStep>
        )}
      </Box>
    </Dialog>
  );
};

export default AssessmentReviewDialog;
